import { useStore } from '@store/index';
import { buildOrgRoutePath, buildRoutePath, removeOrgPrefix } from '@utils/routes';
import React from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { useShouldShowWebhooksPage } from '@/hooks/useShouldShowWebhooksPage';

/**
 * Guards the org-scoped Webhooks routes with the same gate as the sidebar entry
 * (feature flag `webhooks_<org_id>` + ListWebhookEndpoints permission at
 * organization scope), so a bookmarked URL cannot bypass it. Waits for both the
 * flag and the permission map before deciding, so a deep link is not bounced
 * while they load.
 */
export function WebhooksRouteValidator() {
  const { selectedOrganizationId, selectedProgramId } = useStore();
  const { orgId } = useParams<{ orgId?: string }>();
  const routeOrganizationId = orgId ? removeOrgPrefix(orgId) : null;
  const effectiveOrganizationId = routeOrganizationId || selectedOrganizationId;
  const { shouldShow, isLoading } = useShouldShowWebhooksPage({
    organizationId: effectiveOrganizationId,
  });

  if (isLoading) {
    return null;
  }

  if (!shouldShow || !effectiveOrganizationId) {
    if (effectiveOrganizationId && selectedProgramId) {
      return (
        <Navigate
          to={buildRoutePath('/cases', effectiveOrganizationId, selectedProgramId)}
          replace
        />
      );
    }

    if (effectiveOrganizationId) {
      return <Navigate to={buildOrgRoutePath('', effectiveOrganizationId)} replace />;
    }

    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
